import { apiClient } from "@/lib/api-client";

/**
 * Identity fields only.
 *
 * The backend deliberately withholds vendor contact details from this endpoint
 * — the vendor table is global, and serving contacts here would let any
 * authenticated project member harvest every designer's supplier book. Declared
 * here rather than imported because `VendorIdentity` is backend-local.
 */
export interface VendorIdentity {
	id: string;
	name: string;
	city: string | null;
	state: string | null;
	isVerified: boolean;
}

/**
 * Lists vendors (verified first, then alphabetical), or filters by name when
 * `query` is given. The API requires a filter to be at least two characters.
 */
export async function findVendors(
	query?: string,
	limit = 25,
): Promise<VendorIdentity[]> {
	const response = await apiClient.get("/api/v1/admin/vendors", {
		params: { q: query || undefined, limit },
	});
	return response.data.data.vendors as VendorIdentity[];
}

export async function setVendorVerified(
	vendorId: string,
	isVerified: boolean,
): Promise<VendorIdentity> {
	const response = await apiClient.patch(
		`/api/v1/admin/vendors/${vendorId}/verified`,
		{ isVerified },
	);
	return response.data.data.vendor as VendorIdentity;
}

export interface CreateVendorInput {
	name: string;
	city?: string | null;
	state?: string | null;
	contactName?: string | null;
	contactPhone?: string | null;
	contactEmail?: string | null;
	notes?: string | null;
}

export interface NearMatch {
	id: string;
	name: string;
	city: string | null;
}

/**
 * `POST /api/v1/admin/vendors` runs the same handler and schema as the
 * `POST /api/v1/vendors` designers and contractors use while sourcing
 * materials; the console calls the admin copy because it holds only the admin
 * session. `isVerified` is not accepted here; the create schema doesn't have
 * the field at all, and it stays off until an admin flips it from this page's
 * own verified switch.
 */
export async function createVendor(
	input: CreateVendorInput,
): Promise<{ vendor: VendorIdentity; nearMatches: NearMatch[] }> {
	const response = await apiClient.post("/api/v1/admin/vendors", input);
	return response.data.data as {
		vendor: VendorIdentity;
		nearMatches: NearMatch[];
	};
}
